import { MenuItem, MenuSub } from '@affine/component/ui/menu';
import {
  CreateWorkspaceMenuItems,
  type WorkspaceTemplateOption,
} from '@affine/core/components/create-workspace-menu';
import { AuthService, DefaultServerService } from '@affine/core/modules/cloud';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { ImportIcon, PlusIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';

import * as styles from './index.css';

export const AddWorkspace = ({
  onAddWorkspace,
  onNewWorkspace,
  onSelectTemplate,
  onMoreOptions,
}: {
  onAddWorkspace?: () => void;
  /** used when workspace creation is gated behind sign-in */
  onNewWorkspace?: () => void;
  onSelectTemplate?: (template: WorkspaceTemplateOption | null) => void;
  onMoreOptions?: () => void;
}) => {
  const t = useI18n();
  const defaultServerService = useService(DefaultServerService);
  const session = useLiveData(useService(AuthService).session.session$);
  const enableLocalWorkspace = useLiveData(
    defaultServerService.server.config$.selector(
      c =>
        c.features.includes(ServerFeature.LocalWorkspace) ||
        BUILD_CONFIG.isNative
    )
  );
  const canCreate =
    session.status === 'authenticated' || !!enableLocalWorkspace;

  const label = enableLocalWorkspace
    ? t['com.affine.workspaceList.addWorkspace.create']()
    : t['com.affine.workspaceList.addWorkspace.create-cloud']();

  return (
    <>
      {BUILD_CONFIG.isElectron && (
        <MenuItem
          block={true}
          prefixIcon={<ImportIcon />}
          prefixIconClassName={styles.prefixIcon}
          onClick={onAddWorkspace}
          data-testid="add-workspace"
          className={styles.ItemContainer}
        >
          <div className={styles.ItemText}>
            {t['com.affine.workspace.local.import']()}
          </div>
        </MenuItem>
      )}
      {canCreate && onSelectTemplate ? (
        // pick the template right here — no create-workspace dialog in between
        <MenuSub
          triggerOptions={{
            block: true,
            prefixIcon: <PlusIcon />,
            prefixIconClassName: styles.prefixIcon,
            className: styles.ItemContainer,
            'data-testid': 'new-workspace',
          }}
          items={
            <CreateWorkspaceMenuItems
              onSelectTemplate={onSelectTemplate}
              onMoreOptions={onMoreOptions}
            />
          }
        >
          <div className={styles.ItemText}>{label}</div>
        </MenuSub>
      ) : (
        <MenuItem
          block={true}
          prefixIcon={<PlusIcon />}
          prefixIconClassName={styles.prefixIcon}
          onClick={onNewWorkspace}
          data-testid="new-workspace"
          className={styles.ItemContainer}
        >
          <div className={styles.ItemText}>{label}</div>
        </MenuItem>
      )}
    </>
  );
};
